import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X';
import { userProfileData } from "./profileInfo";
import { fetchQuery, getCurrentTaxYear } from "../functions/misc";
import { CreateProfileConsents } from "../queries/CreateProfileConsents";
import useProfileStore from "../store/ProfileStore";
import { GetOptimaEmails } from "../queries/GetOptimaEmails";
import { clientConfig } from "../awsClientConfig";

export interface ItemModalProps {
    onClose: () => void;
    onAgree: () => void;
    open: boolean;
  }
  
  export function EConsentModal({ onClose, onAgree, open = false }: ItemModalProps): React.JSX.Element {

    const [showFullAgreement, setShowFullAgreement] = useState(false);
    const [loading, setLoading] = useState(false);
    const { setEConsent } = useProfileStore();
    const [fullAgreementPart2, setFullAgreementPart2] = useState("");
    const client = new clientConfig();

    useEffect(() => {
      getSoftwareEmail();
    }, []);

    const getSoftwareEmail = async (): Promise<void> => {
      const res: any = await client.query({ query: GetOptimaEmails });
      const emails = res?.data?.GetOptimaEmails?.Emails || [];
      if (emails && emails.length) {
        const softwareEmail = emails.find((email: string) =>
          email.toLowerCase().includes("software")
        );

        const full_agreement_part2 = `<p style={{marginBottom: '1rem', fontSize: '12px'}}>
                    <b>Definitions.</b> For purposes of this Agreement the words "we," "us," "our," and "Optima Tax Relief" refer to Optima Tax Relief, LLC, and each and every current and future affiliate of Optima Tax Relief, LLC, and the words "you" and "your" mean you, the prospective or current Optima Tax Relief client(s) and/ or customer(s); the individual(s) giving this consent; and also each authorized signer and/or authorized representative. In this Agreement, "Communications" means each disclosure, notice, agreement, fee schedule, statement, record, document, and other information we provide to you, or that you sign or submit or agree to at our request. Additionally, "Optima Tax Relief Product" means each and every account, product or service we offer, electronically or otherwise, that you apply for, own, use, administer or access, electronically or otherwise, either now or in the future.
                    </p>
                    <p style={{marginBottom: '1rem', fontSize: '12px'}}>
                    <b>Scope of Communications Covered by this Agreement; Privacy Policies.</b> Your consent covers all Communications relating to any Optima Tax Relief Product (including, but not limited to, legal and regulatory disclosures and communications associated with the product; periodic disclosures or monthly billing statements; notices or disclosures about a change in the terms of the product or associated payment feature; and privacy policies and notices) if you have affirmatively consented to receive such records electronically and you have not withdrawn such consent. You agree that by keeping our Privacy Policy available for review on https://www.optimataxrelief.com, we may satisfy our obligation to provide you with an annual copy of our Privacy Policy.
                    </p>
                    <p style={{marginBottom: '1rem', fontSize: '12px'}}>
                    <b>Method of Providing Communications.</b> All Communications may be delivered to you in a variety of ways. All Communications may be provided to you in electronic form in any or all of the following ways: via electronic mail message ("e-mail"); by access to a website that we will designate in an e-mail notice we send to you at the time the information is available; or to the extent permissible by law, by access to a website that we will generally designate in advance for such purpose. We may always, in our sole discretion, provide you with any Communication in writing, even if you have chosen to receive it electronically. Sometimes, the law, or our agreement with you, requires you to give us a written notice; you must still provide these notices to us on paper, unless we specifically tell you in another Communication how you may deliver that notice to us electronically.
                    </p>
                    <p style={{marginBottom: '1rem', fontSize: '12px'}}>
                    <b>Requesting Paper Copies.</b> We will not send you a paper copy of any Communication unless you request it or we otherwise deem it appropriate to do so. You can obtain a paper copy of an electronic Communication(s) by printing and/or downloading and saving it yourself or by requesting that we mail you a paper copy. A fee may be incurred for the paper copy unless charging a fee is prohibited by applicable law. To request a paper copy, contact us in one of the following ways: contacting us at (800) 536-0734 or by e-mail at customerservice@optimataxrelief.com.  Please be sure to specify the document(s) you wish to receive and state your request for a paper copy of the document(s).
                    </p>
                    <p style={{marginBottom: '1rem', fontSize: '12px'}}>
                    <b>Withdrawing Consent; Consequences of Withdrawing Consent.</b> You have the right to withdraw your consent to receive/obtain electronic Communications at any time. Please be aware that withdrawal of your consent may result in any or all of the following: the charging of higher or additional fees for Optima Tax Relief Products; the restriction or termination of your access to and/or ability to use certain Optima Tax Relief Products, including the Client Profile on Optima Tax Relief’s Client Portal; and/or the termination of your relationship with us. Any withdrawal of consent to receive electronic Communications will be effective only after our receipt of your withdrawal and a reasonable period of time to process it. To withdraw your consent, you must e-mail us at <a href="mailto:${softwareEmail}">${softwareEmail}</a> and state that you wish to withdraw your consent to electronic Communications.
                    </p>
                    <p style={{marginBottom: '1rem', fontSize: '12px'}}>
                    <b>Notifying Us of a Change in your E-mail Address.</b> It is your responsibility to provide us with true, accurate, and complete e-mail address, contact, and other information related to this Agreement and your Account(s), and to maintain and promptly update any changes in this information in one of the following ways: if a client, by logging on to your Client Profile on Optima Tax Relief’s Client Portal and selecting the account tab and selecting the e-mail address or contact information you wish to change; or for all others, by contacting our Customer Service Department at (800) 536-0734.
                    </p>
                    <p style={{marginBottom: '1rem', fontSize: '12px'}}>
                    <b>Communications in Writing.</b> You accept all Communications provided by Optima Tax Relief in either electronic or paper format from us to you as reasonable and proper notice for the purpose of any and all laws, rules, and regulations, and agree all such Communications satisfy any requirement that such communications be provided to you in writing or in a form that you may keep.
                    </p>
                    <p style={{marginBottom: '1rem', fontSize: '12px'}}>
                    <b>Hardware and Software Requirements.</b> In order to access, view, and retain electronic Communications, you must have access to the following minimum requirements: one of the following browser or later -- Google Chrome 49+, Mozilla Firefox 46+, Internet Explorer 11, Microsoft Edge 13+, Apple Safari 8+, Opera 27+, Opera Mini 5.0+ ; a connection to the Internet; a version of a program that accurately reads and displays PDF files (such as Adobe Acrobat Reader) that is currently supported by its publisher; and a computer and operating system capable of supporting all of the above. If you wish to print out and retain records on paper or store records electronically, you will also need a printer or electronic storage, respectively. You are responsible for installation, maintenance, and operation of your computer, browser, software, printer, and electronic storage. By providing your consent, you confirm that you have the hardware and software described above, you are able to access and retain any electronic notice(s)/record(s), and you have an active email account.
                    </p>
                    <p style={{marginBottom: '1rem', fontSize: '12px'}}>
                    <b>Changes to Hardware and/or Software Requirements.</b> If our hardware or software requirements change creating a material risk that you would not be able to access or retain electronic Communications, we will give you notice of the revised hardware or software requirements. Continuing to use Electronic Services after receiving notice of the change is reaffirmation of your consent.
                    </p>`;
        setFullAgreementPart2(full_agreement_part2);
      }
    }


    const full_agreement_part1 = `<p style={{marginBottom: '1.2rem', fontSize: '14px'}}>
                E-SIGN CONSENT AND NOTICE REGARDING ELECTRONIC COMMUNICATIONS AND SIGNATURES ("Agreement")
                </p>
                <p style={{marginBottom: '1rem', fontSize: '12px'}}>
                <b>BY CLICKING "I AGREE" BELOW</b> You are accepting the terms of the E-SIGN Consent and Notice Regarding Electronic Communications and Signatures ("Agreement") and thus consenting to electronic delivery of this Agreement and all other notices or disclosures as set forth in this Agreement. You agree that your electronic acceptance of this Agreement or your electronic signature on or acceptance of any other agreement, contract, disclosure, etc. provided electronically is the legal equivalent of your manual signature and by signing you agree to be legally bound by the terms and conditions herein or thereof, respectively.
                </p>`;

    const onReadMoreClickHandler = () => {
        setShowFullAgreement(true);
    }

    const onAgreeClickHandler = async () => {
      setLoading(true);
      try{
        const profileId = await userProfileData();
        const variables = {
          ProfileId: profileId,
          Type: "eConsent",
        };
        await fetchQuery(variables, CreateProfileConsents);
        setEConsent(true);
      }catch(error){
        console.log(error);
        setLoading(false);
      }
      setLoading(false);
      onAgree();
    }

    const onPrintHandler = () => {
      const tempHtml =
        "<html><head><scri" +
        "pt>function step1(){\n" +
        "setTimeout('step2()', 10);}\n" +
        "function step2(){window.print();window.close()}\n" +
        "</scri" +
        "pt></head><body onload='step1()'>\n" +
        "<div>" +
        full_agreement_part1 + 
        "</div>" +
        "<div>" +
        fullAgreementPart2 +
        "</div>" +
        "</body></html>";
      var Pagelink = "e-consent";
      var pwa: any = window.open(Pagelink, "_new");
      pwa.document.open();
      pwa.document.write(tempHtml);
      pwa.document.close();
    }

    
    
    return (
      <React.Fragment>
        <Dialog
          maxWidth="md"
          onClose={onClose}
          open={open}
          sx={{
            '& .MuiDialog-container': { justifyContent: 'flex-center', alignContent: 'flex-start' },
          }}
        >
          <DialogTitle sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, p: 0 }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: 'center',
                flex: '0 0 auto',
                justifyContent: 'space-between',
                py: 1,
                px:3
              }}
            >
              <Typography variant="h6">E-Consent</Typography>
              <IconButton onClick={onClose}>
                <XIcon />
              </IconButton>
            </Stack>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, p: 0 }} dividers>
            <Stack spacing={2} sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 3, py: 2 }}>
              <Box
                sx={{
                  display: 'inline',
                  flex: '0 0 auto',
                  justifyContent: 'left',
                }}
              >
                <p style={{marginBottom: '1.2rem', fontSize: '14px'}}>
                E-SIGN CONSENT AND NOTICE REGARDING ELECTRONIC COMMUNICATIONS AND SIGNATURES ("Agreement")
                </p>
                <p style={{marginBottom: '1rem', fontSize: '12px'}}>
                <b>BY CLICKING "I AGREE" BELOW</b> You are accepting the terms of the E-SIGN Consent and Notice Regarding Electronic Communications and Signatures ("Agreement") and thus consenting to electronic delivery of this Agreement and all other notices or disclosures as set forth in this Agreement. You agree that your electronic acceptance of this Agreement or your electronic signature on or acceptance of any other agreement, contract, disclosure, etc. provided electronically is the legal equivalent of your manual signature and by signing you agree to be legally bound by the terms and conditions herein or thereof, respectively.
                {!showFullAgreement && 
                <a href="#" onClick={onReadMoreClickHandler} style={{color: '#529AF9', marginLeft: '0.2rem'}}>Read More</a>
                }
                </p>
                {showFullAgreement && 
                <div dangerouslySetInnerHTML={{ __html: fullAgreementPart2 }} />
                }
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{px: 3, justifyContent: 'space-between'}}>
            <div>
            <Button onClick={onPrintHandler} className="option-primary-button" sx={{py: '4px'}} disabled={loading}>
              Print
            </Button>
            </div>
            <div>
            <Button onClick={onAgreeClickHandler} className="option-primary-button" sx={{py: '4px'}} disabled={loading}>
              I Agree
            </Button>
            <Button onClick={onClose} sx={{color: '#667085', ml: 1}}>
              Close
            </Button>
          </div>
        </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  }